import { BellIcon, CheckCheck, Inbox } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import useSWR from 'swr'

import { EmptyState } from '@/components/EmptyState'
import { BellIcon as AnimatedBell, type BellIconHandle } from '@/components/ui/animated-icons/bell'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { apiFetcher } from '@/data/fetcher'
import { usePagination } from '@/hooks/usePagination'
import { Collection } from '@/interfaces/Collection'
import { Notification } from '@/interfaces/Notification'
import API from '@/lib/api'

import { notificationTypeWeight, weightToColorMap } from './colors'
import { NotificationItem } from './NotificationItem'

export const Notifications = () => {
  const [includeRead, setIncludeRead] = useState(false)

  const pagination = usePagination({
    perPage: 5,
  })

  const {
    data: notifications,
    error,
    isLoading,
    mutate,
  } = useSWR<Collection<Notification>>(
    `/api/v2/notifications?limit=${pagination.limit}&offset=${pagination.offset}${includeRead ? '&unread=false' : ''}`,
    apiFetcher,
  )

  const hasUnreadNotifications: number | false =
    notifications?.data.reduce(
      (acc, notification): number | false =>
        notification.readAt === null
          ? Math.max(notificationTypeWeight[notification.type], acc || 0)
          : acc,
      false,
    ) ?? false

  // Zvonek jednou zacinká, když přibude nepřečtená notifikace (ne při prvním načtení).
  const unreadCount = notifications?.data.filter((n) => n.readAt === null).length
  const bellRef = useRef<BellIconHandle>(null)
  const prevUnread = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (unreadCount === undefined) return
    if (prevUnread.current !== undefined && unreadCount > prevUnread.current) bellRef.current?.startAnimation()
    prevUnread.current = unreadCount
  }, [unreadCount])

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          className="relative rounded-full p-2 hover:bg-muted transition-colors"
        >
          <AnimatedBell ref={bellRef} size={20} aria-hidden />
          {hasUnreadNotifications && (
            <span
              className={`absolute top-1 right-1 w-2 h-2 rounded-full ${weightToColorMap[hasUnreadNotifications]}`}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 overflow-hidden p-0">
        <div className="flex items-center justify-between border-b px-4 py-2.5">
          <span className="text-sm font-medium">Notifikace</span>
          {hasUnreadNotifications && (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
              onClick={() => {
                const newNotifications = notifications!.data.map((notification) => ({
                  ...notification,
                  readAt: new Date().toISOString(),
                }))

                API.post(`/api/v2/read-notifications`)
                  .then(() => {
                    mutate({ data: newNotifications, total: newNotifications.length }, false)
                  })
                  .catch((err) => {
                    console.error(err)
                    toast.error('Notifikace se nepodařilo označit jako přečtené.')
                  })
              }}
            >
              <CheckCheck className="size-3.5" />
              Označit vše jako přečtené
            </button>
          )}
        </div>
        <div className="flex max-h-[70vh] flex-col gap-1 overflow-y-auto p-2">
          {!isLoading && !error && notifications && notifications.data.length === 0 && (
            <EmptyState
              compact
              icon={includeRead ? Inbox : BellIcon}
              title={includeRead ? 'Zatím žádné notifikace' : 'Všechno máte vyřízené'}
              description={
                includeRead
                  ? 'Až přijde platba nebo se změní stav dokladu, dáme vám vědět tady.'
                  : 'Nové platby, upomínky a změny stavů dokladů se objeví tady.'
              }
              ctaButton={
                !includeRead ? (
                  <Button variant="ghost" size="sm" onClick={() => setIncludeRead(true)}>
                    Zobrazit starší
                  </Button>
                ) : undefined
              }
            />
          )}
          {!isLoading &&
            !error &&
            notifications &&
            notifications?.data.map((notification) => {
              return (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onRead={(notification) => {
                    const newNotifications = notifications.data.map((n) => {
                      if (n.id === notification.id) {
                        return { ...n, readAt: new Date().toISOString() }
                      }
                      return n
                    })

                    API.post(`/api/v2/read-notifications/${notification.id}`)
                      .then(() => {
                        mutate(
                          {
                            data: newNotifications,
                            total: notifications.total,
                          },
                          false,
                        )
                      })
                      .catch((err) => {
                        console.error(err)
                        toast.error(
                          'Notifikaci se nepodařilo označit jako přečtenou.',
                        )
                      })
                  }}
                />
              )
            })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
